/**
 * #1730 (review of PR #1745): what a Snowflake warehouse credential check (`warehouse` target)
 * says on the Destination screen and in the credential email for `missing_object`.
 *
 * The check stores `missing_object` for three states, each read before or without a load: the
 * private key's secret is not in the vault, the saved private key or its passphrase cannot be used
 * by the driver (no request reaches Snowflake), and, from a Test, the database the binding names
 * does not exist or is not granted. The load's sentence ("A table or stage the load needs does not
 * exist") is true of none of them, so this target reads its own. The Test and the daily check
 * record a key the driver cannot use with this one cause (`runner/credentials/checkers.ts`
 * `isPrivateKeyError`, `server/services/destinationTest.ts` `checkOutcomeOfTest`).
 *
 * The outcome is unchanged: only the words change. Shared, so the runner's renderer and the
 * client read one rule.
 */

interface CheckTarget {
  targetType: string;
  causeCode?: string | null;
}

/** The daily check's cause for a `warehouse` target, in the form of the other tables (no full stop). */
const WAREHOUSE_CHECK_SENTENCES: Readonly<Record<string, string>> = {
  missing_object:
    "The saved private key could not be read or used, or the database in Destination does not exist or is not granted",
};

/** The cause sentence for a `warehouse` target, or null when its cause has no warehouse words. */
export function warehouseCheckSentence(target: CheckTarget): string | null {
  if (target.targetType !== "warehouse" || !target.causeCode) return null;
  return Object.hasOwn(WAREHOUSE_CHECK_SENTENCES, target.causeCode)
    ? (WAREHOUSE_CHECK_SENTENCES[target.causeCode] ?? null)
    : null;
}
